/**
 * Three.js character + static-prop layer (#237, first slice of #231; props
 * #816) — renders champion GLBs and static terrain props live in the arena,
 * sharing PixiJS's WebGL context per the official PixiJS "mixing three and
 * pixi" pattern (one context; Three draws characters + props, Pixi keeps arena
 * floor / VFX / UI). No separate canvas, no offscreen compositing.
 *
 * #816: props (pillars) live in the SAME scene as the characters, so the GPU's
 * DEPTH BUFFER resolves occlusion per-pixel — a champion BEHIND a pillar is
 * hidden, a champion IN FRONT of one is visible — with no sorting logic and no
 * extra Pixi pass (a later Pixi pass would wrongly occlude champions standing
 * in front of a pillar too). Health bars / nameplates stay in Pixi's overlay
 * pass, which draws AFTER this one and is never depth-tested, so an occluded
 * champion is still locatable.
 *
 * Ownership: PixiJS creates the canvas + WebGL2 context (`createStage`); we
 * attach a `THREE.WebGLRenderer` to that SAME context by re-requesting it from
 * the shared canvas (`getContext('webgl2')` returns the existing context). The
 * mount loop drives THREE passes each frame (#243): Pixi world-below (floor +
 * ground, clears the canvas) → Three characters + props (composited ON TOP —
 * the color buffer is preserved, only depth/stencil are cleared for this pass)
 * → Pixi overlay-above (entities/fx/bars/nameplates, `clear: false`).
 * `resetState()` hands GL state cleanly between the two renderers.
 *
 * RIP-OUT (CHARACTERS ONLY): `createThreeCharacterLayer` returns `null` if the
 * shared context is unavailable; the caller keeps Pixi on its normal
 * auto-render path. Combined with `parseCharacterModel` returning `null` for a
 * missing/bad GLB, deleting a CHARACTER GLB returns that champion to today's 2D
 * visuals with no crash.
 *
 * PROPS ARE NOT RIP-OUTTABLE (#829). The 2D pillar art is gone, so there is
 * nothing for a missing/bad prop GLB to fall back to — `loadPropModel` rejects
 * loudly instead (see `propModel.ts`), and a match simply never reaches
 * `setProps` with a broken asset.
 *
 * PRESENTATION ONLY — reads interpolated (cosmetic) state; never the sim/hash.
 * Facing rotation is a smoothed cosmetic lerp toward the sim facing sector and
 * never feeds back into anything hashed.
 */

import * as THREE from "three";
import { clone as cloneSkeleton } from "three/examples/jsm/utils/SkeletonUtils.js";
import type { Application } from "pixi.js";
import { BG_COLOR } from "../colors.js";
import { MOVE_EPSILON } from "../entityView.js";
import type { ProjectionCenter } from "../projection.js";
import {
  CHARACTER_3D_ANIMATION_NAMES,
  CROSSFADE_SECONDS,
  FALLBACK_3D_ANIMATION,
  applyAnimationTransition,
  choose3DAnimation,
  computeLiveRadiusScale,
  findClip,
  type Character3DAnimation,
  type LoadedCharacterModel,
} from "./characterModel.js";
import { createIsoCamera, facingToYaw, simToWorld3, updateIsoCamera } from "./isoCamera.js";
import { reconcileGroundMarkers, type GroundMarkerSubject } from "./groundMarker.js";
import { instantiateProp, type LoadedPropModel } from "./propModel.js";

/** Per-frame cosmetic state for one 3D character. */
export interface Character3DInput {
  readonly id: string;
  /** Interpolated world position (sim units). */
  readonly wx: number;
  readonly wy: number;
  /** Sim facing sector (0..FACING_SECTORS-1). */
  readonly facing: number;
  readonly alive: boolean;
  /** Team index — the ONLY reason this field exists is the #825 ground
   *  marker's `teamColor(entity.team)` tint (the same palette the 2D body fill
   *  uses). Client-internal render input; no wire/protocol shape changes. */
  readonly team: number;
  /** Entity is mid-cast (`cast !== null` on the snapshot) — the same cosmetic,
   *  snapshot-derived flag the 2D sprite path feeds `chooseAnimation`. Selects
   *  the `cast` clip on a GLB that ships one. */
  readonly casting: boolean;
  /** Live, aura-modified body radius, world units (#342 — e.g. Stand Behind
   *  Me's +30%). The GLB's baked scale already normalises it to
   *  `CHARACTER_TARGET_HEIGHT_UNITS` at `DEFAULT_BODY_RADIUS_UNITS`
   *  (`characterModel.ts`); `syncCharacters` layers a live multiplier —
   *  `radius / DEFAULT_BODY_RADIUS_UNITS` — on TOP of that baked normalization
   *  so a size-changing statMod visibly scales the model too, not just the
   *  2D fallback paths. */
  readonly radius: number;
  /** The parsed character GLB this instance is cloned from (#818). Carried PER
   *  INSTANCE — mirroring `Prop3DInput.model` — so one frame can mix champions
   *  (a rogue and a fighter) without new render code. */
  readonly model: LoadedCharacterModel;
}

/**
 * One static prop placement (#816) — a pillar today, any static terrain
 * geometry tomorrow (#488's walls, an ice wall). Unlike `Character3DInput`
 * (reconciled every frame) a prop set is pushed ONCE per arena load via
 * `setProps`, because props are static map data.
 *
 * INTERFACE STABILITY: v1. The fields below are the ones the registration +
 * height guarantees need (`propModel.ts`), and a second prop kind needs only
 * DATA (`props.ts`) to use them. A prop that must be oriented (a wall) will
 * need a `yaw` here — deliberately NOT added speculatively, so treat the shape
 * as v1 rather than final.
 */
export interface Prop3DInput {
  /** Stable per-instance id (e.g. `pillar:0`), for diffing/debugging. */
  readonly id: string;
  /** The parsed prop GLB this instance is cloned from. Carried PER INSTANCE so
   *  one prop set can mix kinds (pillars + walls) without new render code. */
  readonly model: LoadedPropModel;
  /** Prop centre on the sim ground plane (map data). */
  readonly wx: number;
  readonly wy: number;
  /** The COLLISION radius from map data — the instance's base radius == this. */
  readonly radiusUnits: number;
  /** Height above the floor, derived from the map radius (`propHeightUnits`). */
  readonly heightUnits: number;
}

/** Speed of the cosmetic facing lerp (per second, as a smoothing rate). */
const FACING_LERP_RATE = 12;

interface CharacterInstance {
  readonly root: THREE.Object3D;
  readonly mixer: THREE.AnimationMixer;
  /** Clip name → action, for every `Character3DAnimation` this GLB actually
   *  ships. A clip the file omits is simply ABSENT from the map, which
   *  `setAnimation` treats as "keep playing the current one" — the same degrade
   *  the hardcoded two-action version had, generalised past `idle`/`walk`. */
  readonly actions: ReadonlyMap<Character3DAnimation, THREE.AnimationAction>;
  /** The model this instance was cloned from, so a seat that swaps champion is
   *  rebuilt against the new GLB instead of keeping the stale mesh. */
  readonly model: LoadedCharacterModel;
  current: Character3DAnimation;
  yaw: number;
  /** Last world position, for the walk/idle motion test. */
  prevWx: number | null;
  prevWy: number | null;
}

/** The live three character + prop layer. */
export interface ThreeCharacterLayer {
  /** Reconcile character instances against the frame and advance mixers. */
  syncCharacters: (chars: readonly Character3DInput[], dtSeconds: number) => void;
  /** Replace the whole static prop set (#816) — called at arena load (and again
   *  when the arena is swapped post-mount), NOT per frame. Passing `[]` removes
   *  every prop, which is also the rip-out path when no prop GLB loaded. */
  setProps: (props: readonly Prop3DInput[]) => void;
  /** Aim the camera to match the 2D projection for this viewport + centre. */
  updateCamera: (viewportW: number, viewportH: number, center: ProjectionCenter) => void;
  /** Render the 3D scene on top of Pixi's world-below pass (floor/ground)
   *  and before Pixi's overlay-above pass (#243). */
  renderScene: () => void;
  /** Tear down the Three renderer WITHOUT destroying the shared GL context
   *  (Pixi owns it). */
  destroy: () => void;
}

/** Lerp an angle toward `target` along the shortest arc. */
function lerpAngle(from: number, target: number, t: number): number {
  let delta = (target - from) % (2 * Math.PI);
  if (delta > Math.PI) delta -= 2 * Math.PI;
  if (delta < -Math.PI) delta += 2 * Math.PI;
  return from + delta * t;
}

/**
 * Create the shared-context Three character layer over `pixiApp`. Returns `null`
 * if the WebGL2 context can't be re-acquired from the shared canvas (rip-out).
 */
export function createThreeCharacterLayer(pixiApp: Application): ThreeCharacterLayer | null {
  const canvas = pixiApp.canvas;
  // Re-request the SAME context Pixi already created; a second getContext with
  // the same type returns the existing context (never a new one).
  const gl = canvas.getContext("webgl2");
  if (gl === null) return null;

  const renderer = new THREE.WebGLRenderer({
    canvas,
    // @types/three types `context` as WebGLRenderingContext; three accepts and
    // auto-detects the WebGL2 context at runtime.
    context: gl as unknown as WebGLRenderingContext,
    antialias: true,
    stencil: true, // Pixi masks need a stencil buffer on the shared context.
    alpha: true,
  });
  // #243: Pixi's world-below pass (floor + ground) draws BEFORE this layer,
  // so autoClear must NOT wipe the color buffer — only depth/stencil are
  // cleared per frame (see `renderScene`), keeping the floor visible under
  // the characters instead of Pixi's full-stage repaint occluding them.
  renderer.autoClear = false;
  renderer.setClearColor(BG_COLOR, 1);
  renderer.shadowMap.enabled = false;

  const scene = new THREE.Scene();
  // Simple, cheap lighting so the MeshStandard material reads with form.
  const hemi = new THREE.HemisphereLight(0xffffff, 0x404050, 1.1);
  const key = new THREE.DirectionalLight(0xffffff, 1.4);
  key.position.set(-1, 2, 1.5); // roughly from the iso camera side
  scene.add(hemi, key);

  const camera = createIsoCamera();
  const instances = new Map<string, CharacterInstance>();
  // #825: one ground marker (hitbox ring + facing wedge) per LIVE character,
  // in this same scene so the shared depth buffer lets the model occlude the
  // marker's far arc. Keyed by entity id and reconciled by
  // `reconcileGroundMarkers` inside `syncCharacters` — never parented to the
  // character root, whose own scale (#342) and yaw would double-apply.
  const groundMarkers = new Map<string, THREE.Object3D>();
  // #816: static props (pillars) share this scene — and therefore the depth
  // buffer — with the characters. Replaced wholesale by `setProps`.
  const propRoots: THREE.Object3D[] = [];

  let lastW = -1;
  let lastH = -1;
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio : 1;
  renderer.setPixelRatio(dpr);

  function makeInstance(model: LoadedCharacterModel): CharacterInstance {
    const root = cloneSkeleton(model.root);
    scene.add(root);
    const mixer = new THREE.AnimationMixer(root);
    // Build an action per clip the GLB actually carries. Every action is
    // `play()`ed once at weight 0 (except the initial `idle`) so `crossFadeTo`
    // always has a running action on both sides — the same lifecycle the
    // two-clip version used, just data-driven.
    const actions = new Map<Character3DAnimation, THREE.AnimationAction>();
    for (const name of CHARACTER_3D_ANIMATION_NAMES) {
      const clip = findClip(model.clips, name);
      if (clip === null) continue;
      const action = mixer.clipAction(clip);
      // `death` must hold its final frame rather than loop back to standing.
      if (name === "death") {
        action.setLoop(THREE.LoopOnce, 1);
        action.clampWhenFinished = true;
      }
      action.play();
      action.setEffectiveWeight(name === FALLBACK_3D_ANIMATION ? 1 : 0);
      actions.set(name, action);
    }
    return {
      root,
      mixer,
      actions,
      model,
      current: FALLBACK_3D_ANIMATION,
      yaw: 0,
      prevWx: null,
      prevWy: null,
    };
  }

  function setAnimation(inst: CharacterInstance, next: Character3DAnimation): void {
    if (next === inst.current) return;
    const to = inst.actions.get(next);
    if (to === undefined) return; // requested clip absent → keep current
    // Weight handling lives in `applyAnimationTransition` (characterModel.ts)
    // so it is unit-testable without a renderer — see its doc comment for why
    // the pre-fade `setEffectiveWeight(1)` is what makes walk/cast visible.
    // `next` is also the clip identity the playback rate is resolved from
    // (#840) — walk gets `WALK_ANIMATION_RATE`, every other clip gets 1.
    applyAnimationTransition(inst.actions.get(inst.current) ?? null, to, next, CROSSFADE_SECONDS);
    inst.current = next;
  }

  function syncCharacters(chars: readonly Character3DInput[], dtSeconds: number): void {
    const seen = new Set<string>();
    // #825: collected in the same pass, because a marker's yaw is the
    // character instance's OWN lerped yaw (computed below) — sharing it is
    // what guarantees the marker and the model never disagree about facing.
    const markerSubjects: GroundMarkerSubject[] = [];
    for (const c of chars) {
      seen.add(c.id);
      let inst = instances.get(c.id);
      // Rebuild when the seat's model changes (champion swap between rounds), or the
      // instance would keep rendering the previous champion's mesh.
      if (inst !== undefined && inst.model !== c.model) {
        scene.remove(inst.root);
        inst.mixer.stopAllAction();
        instances.delete(c.id);
        inst = undefined;
      }
      if (inst === undefined) {
        inst = makeInstance(c.model);
        inst.yaw = facingToYaw(c.facing);
        instances.set(c.id, inst);
      }
      // Motion test from the previous world position (same threshold the 2D
      // renderer uses). A dead body idles; the 2D layer still dims corpses.
      const moving =
        inst.prevWx !== null &&
        inst.prevWy !== null &&
        (Math.abs(c.wx - inst.prevWx) > MOVE_EPSILON ||
          Math.abs(c.wy - inst.prevWy) > MOVE_EPSILON);
      inst.prevWx = c.wx;
      inst.prevWy = c.wy;

      setAnimation(inst, choose3DAnimation({ alive: c.alive, casting: c.casting, moving }));
      inst.root.position.copy(simToWorld3(c.wx, c.wy, 0));
      // #342: live per-instance size multiplier on top of the model's baked
      // normalization (see `Character3DInput.radius`'s doc comment) — the
      // outer placement root's own `.scale` is otherwise untouched by
      // `normaliseToPlacementRoot` (that bakes its scale onto the INNER
      // `scene` child), so it's free for this live multiplier.
      inst.root.scale.setScalar(computeLiveRadiusScale(c.radius));
      const targetYaw = facingToYaw(c.facing);
      const t = Math.min(1, Math.max(0, dtSeconds * FACING_LERP_RATE));
      inst.yaw = lerpAngle(inst.yaw, targetYaw, t);
      inst.root.rotation.set(0, inst.yaw, 0);
      inst.mixer.update(dtSeconds);
      markerSubjects.push({
        id: c.id,
        team: c.team,
        alive: c.alive,
        wx: c.wx,
        wy: c.wy,
        radius: c.radius,
        yaw: inst.yaw,
      });
    }
    // Remove instances for entities no longer present.
    for (const [id, inst] of instances) {
      if (!seen.has(id)) {
        scene.remove(inst.root);
        inst.mixer.stopAllAction();
        instances.delete(id);
      }
    }
    // #825: add/update/remove ground markers. A DEAD character keeps its
    // (corpse) instance above but loses its marker here — `alive: false` goes
    // down the same removal path as "left the frame", so there is exactly one
    // marker teardown mechanism.
    reconcileGroundMarkers(groundMarkers, scene, markerSubjects);
  }

  /** Detach every prop instance from the scene. Geometry/materials are SHARED
   *  with the `LoadedPropModel` they were cloned from (and with each other), so
   *  they are deliberately not disposed here — the model outlives the set. */
  function clearProps(): void {
    for (const root of propRoots) scene.remove(root);
    propRoots.length = 0;
  }

  function setProps(props: readonly Prop3DInput[]): void {
    clearProps();
    // Input (map array) order — the same order the sim iterates pillars in and
    // the 2D path draws them in. Depth is resolved by the GPU per-pixel, so the
    // add order is presentational bookkeeping only.
    for (const p of props) {
      const root = instantiateProp(p.model, {
        wx: p.wx,
        wy: p.wy,
        radiusUnits: p.radiusUnits,
        heightUnits: p.heightUnits,
      });
      root.name = `Prop:${p.id}`;
      scene.add(root);
      propRoots.push(root);
    }
  }

  function updateCamera(viewportW: number, viewportH: number, center: ProjectionCenter): void {
    if (viewportW !== lastW || viewportH !== lastH) {
      // updateStyle=false: Pixi owns the canvas CSS size (autoDensity).
      renderer.setSize(viewportW, viewportH, false);
      lastW = viewportW;
      lastH = viewportH;
    }
    updateIsoCamera(camera, viewportW, viewportH, center);
  }

  function renderScene(): void {
    renderer.resetState();
    // Depth/stencil only — the color buffer already holds Pixi's world-below
    // pass (floor + ground, #243) and must survive into this draw.
    renderer.clear(false, true, true);
    renderer.render(scene, camera);
  }

  function destroy(): void {
    for (const [, inst] of instances) {
      inst.mixer.stopAllAction();
      scene.remove(inst.root);
    }
    instances.clear();
    // Empty subject list ⇒ every marker takes the normal removal path (#825).
    reconcileGroundMarkers(groundMarkers, scene, []);
    clearProps();
    // Do NOT lose the shared context — Pixi still owns it. `forceContextLoss`
    // would kill Pixi too, so we only drop Three's own GPU resources.
    renderer.dispose();
  }

  return { syncCharacters, setProps, updateCamera, renderScene, destroy };
}
